import { useEffect, useState } from 'react';
import { getLawFirmDetail } from '@/services/organizations';

/** companyType 为 1 时使用律所/律师文案，其余类型使用通用企业文案 */
export const isLawFirmCompany = (companyType: unknown) => {
    if (companyType === null || companyType === undefined || companyType === '') return false;
    return Number(companyType) === 1;
};

const LAW_TO_GENERIC: Record<string, string> = {
    律所名称: '企业名称',
    律师姓名: '姓名',
    擅长领域: '专业领域',
    律师形象: '个人形象',
    '律所 Logo': '企业 Logo',
    律所LOGO: '企业LOGO',
    律所介绍: '企业介绍',
    律所宣传视频: '企业宣传视频',
    执业证号: '工号',
    律所规模: '企业规模',
};

const GENERIC_TO_LAW = Object.fromEntries(
    Object.entries(LAW_TO_GENERIC).map(([law, generic]) => [generic, law]),
);

/** 把接口里已有的字段名，按公司类型显示成对应说法 */
export const localizeTerm = (label: string, companyType: unknown) => {
    const text = String(label || '');
    if (!text) return text;
    if (isLawFirmCompany(companyType)) return GENERIC_TO_LAW[text] || text;
    return LAW_TO_GENERIC[text] || text;
};

export const getOrgCopy = (companyType: unknown) => {
    const law = isLawFirmCompany(companyType);
    return {
        law,
        menu: law ? '律所管理' : '企业管理',
        listDesc: law
            ? '管理当前律所的智能体基础配置，查看已入驻律师。'
            : '管理当前企业的智能体基础配置，查看已入驻成员。',
        configButton: law ? '律所信息配置' : '企业信息配置',
        memberListTitle: law ? '入驻律师' : '入驻成员',
        memberName: law ? '律师姓名' : '姓名',
        searchPlaceholder: law ? '请输入律师姓名或手机号' : '请输入姓名或手机号',
        missingPhoneEdit: law ? '该律师缺少手机号，无法进入填报页' : '该成员缺少手机号，无法进入填报页',
        missingPhoneLink: law ? '该律师缺少手机号，无法生成链接' : '该成员缺少手机号，无法生成链接',
        specialty: law ? '擅长领域' : '专业领域',
        specialtyFill: law ? '请填写擅长领域' : '请填写专业领域',
        specialtyMax: (count: number) => (law ? `擅长领域最多填写 ${count} 个` : `专业领域最多填写 ${count} 个`),
        specialtyEach: (count: number) => (law ? `每个擅长最多 ${count} 个字` : `每个专业领域最多 ${count} 个字`),
        specialtyDup: law ? '擅长领域不能重复' : '专业领域不能重复',
        specialtySample: law ? '擅长领域1，擅长领域2，擅长领域3' : '专业领域1，专业领域2，专业领域3',
        specialtyTag: law ? '擅长领域' : '专业领域',
        specialtyTags: law ? ['擅长1', '擅长2', '擅长3'] : ['领域1', '领域2', '领域3'],
        jobPlaceholder: law ? '如：合伙人律师、执业律师' : '如：产品经理、客户经理',
        nameRequired: law ? '请输入律师姓名' : '请输入姓名',
        namePlaceholder: law ? '请输入律师姓名' : '请输入姓名',
        previewName: law ? '律师姓名' : '姓名',
        previewJob: law ? '律师职位' : '职位',
        licenseLabel: law ? '执业证号' : '工号',
        licenseRequired: law ? '请输入执业证号' : '请输入工号',
        licenseInvalid: '执业证号必须为17位数字',
        licensePlaceholder: law ? '请输入17位执业证号' : '请输入工号',
        licenseMaxLength: law ? 17 : 30,
        licenseTooLong: '工号最长30个字符',
        syncAddress: law ? '同步律所地址' : '同步公司地址',
        experience: law ? '执业经历' : '工作经历',
        experiencePlaceholder: law ? '请输入执业经历' : '请输入工作经历',
        invalidLink: law ? '链接无效，请通过律所生成的填报链接访问' : '链接无效，请通过企业生成的填报链接访问',
        introPending: law ? '律所简介待配置。' : '企业简介待配置。',
        loadMemberFail: law ? '加载律师配置失败，已使用本地缓存数据' : '加载成员配置失败，已使用本地缓存数据',
        phoneNotFound: law ? '未查询到该手机号对应的律师资料' : '未查询到该手机号对应的成员资料',
        templateHint: law
            ? '进入页面时默认选中律所配置的模板样式，律师可在此基础上调整；下方可配置展示字段'
            : '进入页面时默认选中企业配置的模板样式，成员可在此基础上调整；下方可配置展示字段',
        logoAlt: law ? '律所 Logo' : '企业 Logo',
        logoMark: law ? '律' : '企',
        companyName: law ? '律所名称' : '企业名称',
        companyIntro: law ? '律所介绍' : '企业介绍',
        companyIntroPending: law ? '律所介绍待配置' : '企业介绍待配置',
        promoVideo: law ? '律所宣传视频' : '企业宣传视频',
        portrait: law ? '律师形象' : '个人形象',
        certAlt: law ? '执业认证' : '已认证',
        yearsLabel: law ? '执业年限' : '从业年限',
        belongTitle: law ? '所属律所' : '所属企业',
        certified: law ? '已认证律所' : '已认证企业',
        aboutTitle: law ? '关于律所' : '关于企业',
        addressLabel: law ? '律所地址' : '企业地址',
        firmFieldsLabel: law ? '律所字段' : '企业信息',
        scaleLabel: law ? '律所规模' : '企业规模',
        scalePlaceholder: law ? '如：30+ 执业律师' : '如：50-200人',
        introPlaceholder: law
            ? '如：我们致力于为客户提供专业的法律服务，帮助客户解决各种法律问题。'
            : '如：我们致力于为客户提供专业服务，帮助客户解决各类业务问题。',
        businessPlaceholder: law ? '如：企业法律顾问、商事争议解决、劳动用工' : '如：产品研发、客户服务、市场拓展',
        useLogo: law ? '一键使用律所 LOGO' : '一键使用企业 LOGO',
        knowledgeDesc: law
            ? '上传律所资料，帮助智能体提供更精准的回答，官方智能体的知识库会同步给律所所属律师。'
            : '上传企业资料，帮助智能体提供更精准的回答，官方智能体的知识库会同步给企业所属成员。',
        qrAlt: law ? '律所填报二维码' : '企业填报二维码',
        qrFileName: (companyId: string) => (law ? `律所填报二维码-${companyId}.png` : `企业填报二维码-${companyId}.png`),
        loadConfigFail: law ? '加载律所配置失败，请稍后重试' : '加载企业配置失败，请稍后重试',
        uploadBrand: law ? '请先上传律所品牌标识' : '请先上传企业品牌标识',
        emptyEntry: law ? '链接无效，请通过律所管理进入。' : '链接无效，请通过企业管理进入。',
        lead: law
            ? '配置律所统一资料，以及名片和海报的展示样式。生成链接后，律师进入小程序即可看到律所信息并补全个人资料，系统自动同步生成名片与海报。'
            : '配置企业统一资料，以及名片和海报的展示样式。生成链接后，成员进入小程序即可看到企业信息并补全个人资料，系统自动同步生成名片与海报。',
        sectionTitle: law ? '律所信息' : '企业信息',
        sectionHint: law ? '律师访问链接后优先看到这些公司资料' : '成员访问链接后优先看到这些公司资料',
        logoLabel: law ? '律所LOGO' : '企业LOGO',
        logoHelp: law
            ? '请上传用于展示的律所品牌标识，建议使用 PNG/JPG，背景透明更佳。'
            : '请上传用于展示的企业品牌标识，建议使用 PNG/JPG，背景透明更佳。',
        introRequired: law ? '请输入律所介绍' : '请输入企业介绍',
        avatarPlaceholder: law ? '律师\n形象' : '个人\n形象',
        firmAvatarPlaceholder: law ? '律所\n形象' : '企业\n形象',
        cardFixedFields: law ? ['律所名称', '律师姓名', '擅长领域'] : ['企业名称', '姓名', '专业领域'],
        posterFixedFields: law ? ['律所名称', '律师姓名', '擅长领域', '二维码'] : ['企业名称', '姓名', '专业领域', '二维码'],
        firmFieldSummary: law ? '成立时间 · 律所规模 · 主营业务' : '成立时间 · 企业规模 · 主营业务',
    };
};

export type OrgCopy = ReturnType<typeof getOrgCopy>;

const companyTypeCache = new Map<string, Promise<unknown>>();

export const loadCompanyType = (firmId: string) => {
    const id = firmId.trim();
    if (!id) return Promise.resolve(undefined);
    const cached = companyTypeCache.get(id);
    if (cached) return cached;
    const pending = getLawFirmDetail(id)
        .then((detail) => detail.lawFirmConfigDO?.companyType)
        .catch(() => {
            companyTypeCache.delete(id);
            return undefined;
        });
    companyTypeCache.set(id, pending);
    return pending;
};

/** 当前登录组织还没有详情数据时，用律所详情里的 lawFirmConfigDO.companyType */
export const useOrgCopy = (firmId?: string) => {
    const [companyType, setCompanyType] = useState<unknown>();

    useEffect(() => {
        const id = String(firmId || '').trim();
        if (!id) return undefined;
        let cancelled = false;
        loadCompanyType(id).then((type) => {
            if (!cancelled) setCompanyType(type);
        });
        return () => {
            cancelled = true;
        };
    }, [firmId]);

    return getOrgCopy(companyType);
};
